import {
  REMOTE_CONFIG_BANNER_AUDIENCES,
  REMOTE_CONFIG_BANNER_COLORS,
} from 'src/constants/remoteConfig'
import type {
  RemoteConfigBanner,
  RemoteConfigBannerAudience,
  RemoteConfigBannerColor,
} from 'src/interfaces/remoteConfig'

const isNonEmptyString = (value: unknown): value is string => {
  return typeof value === 'string' && value.trim().length > 0
}

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

// `Date.parse` reads a date-only string as UTC midnight, which is the previous evening in the US
const parseBannerDate = (value: unknown, boundary: 'start' | 'end'): number | null => {
  if (!isNonEmptyString(value)) {
    return null
  }

  const dateOnlyMatch = DATE_ONLY_PATTERN.exec(value)

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch.map(Number)

    return boundary === 'start'
      ? new Date(year, month - 1, day).getTime()
      : new Date(year, month - 1, day, 23, 59, 59, 999).getTime()
  }

  const timestamp = Date.parse(value)

  return Number.isFinite(timestamp) ? timestamp : null
}

export const parseRemoteConfigBanner = (value: unknown): RemoteConfigBanner | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null
  }

  const { id, title, text, startDate, endDate, color, dismissible, audience } = value as Record<
    string,
    unknown
  >

  if (!isNonEmptyString(id) || !isNonEmptyString(text)) {
    return null
  }

  const startsAt = parseBannerDate(startDate, 'start')
  const endsAt = parseBannerDate(endDate, 'end')

  if (startsAt === null || endsAt === null || startsAt > endsAt) {
    return null
  }

  const isAudienceValid =
    audience === undefined ||
    REMOTE_CONFIG_BANNER_AUDIENCES.includes(audience as RemoteConfigBannerAudience)

  // A typo must not widen a targeted banner to everyone
  if (!isAudienceValid) {
    return null
  }

  return {
    id,
    title: isNonEmptyString(title) ? title : '',
    text,
    startsAt,
    endsAt,
    color: REMOTE_CONFIG_BANNER_COLORS.includes(color as RemoteConfigBannerColor)
      ? (color as RemoteConfigBannerColor)
      : 'info',
    dismissible: typeof dismissible === 'boolean' ? dismissible : true,
    audience: (audience as RemoteConfigBannerAudience | undefined) ?? 'all',
  }
}

export const isRemoteConfigBannerInWindow = (banner: RemoteConfigBanner, now: number): boolean => {
  return banner.startsAt <= now && now <= banner.endsAt
}

/** The next moment the banner shows or hides, or null once its window has passed. */
export const getRemoteConfigBannerNextBoundary = (
  banner: RemoteConfigBanner,
  now: number,
): number | null => {
  if (now < banner.startsAt) {
    return banner.startsAt
  }

  if (now <= banner.endsAt) {
    return banner.endsAt + 1
  }

  return null
}
